import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CoWorkSlider from "../components/cowork/CoWorkSlider";
import JobCard from "../components/cowork/JobCard";
import AuthModal from "../components/cowork/AuthModal";
import MemberShell from "../components/cowork/MemberShell";
import SellerSearchModal from "../components/cowork/SellerSearchModal";
import { openChat } from "../components/cowork/chatActions";
import useCoWorkUser from "../hooks/useCoWorkUser";
import useCoWorkBranding from "../hooks/useCoWorkBranding";
import { apiRequest, fetchCoWorkSlides, fetchCoWorkSettings } from "../api";
import { cardClass } from "../components/cowork/jobUtils";

const inputClass = "px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";

// The Co-Work page: banner slider and the public jobs board. Visitors see a "Register" button; signed-in
// buyers / sellers get their profile sidebar on the left. "Find Seller" (banner button, or /co-work?find=1)
// opens the seller search in a window. Everything editable lives in the admin panel under Co-Work.
export default function CoWorkPage() {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { user, refresh, signOut } = useCoWorkUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobsRef = useRef(null);

  const [slides, setSlides] = useState(null);   // null = still loading
  const [settings, setSettings] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [findOpen, setFindOpen] = useState(searchParams.get("find") === "1");
  const [modal, setModal] = useState(null);   // { mode, role } | null
  const [notice, setNotice] = useState("");

  useEffect(() => {
    fetchCoWorkSlides().then(setSlides).catch(() => setSlides([]));
    fetchCoWorkSettings().then(setSettings).catch(() => {});
  }, []);

  useCoWorkBranding(settings);
  useEffect(() => {
    document.title = settings?.browser_title || "Co-Work Marketplace";
  }, [settings]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      const params = new URLSearchParams();
      if (category) params.set("category", category);
      if (query.trim()) params.set("q", query.trim());
      const { ok, data } = await apiRequest(`/core/cowork/jobs/?${params}`);
      setJobs(ok ? data : []);
    }, 250);
    return () => clearTimeout(timer);
  }, [category, query]);

  // Links like "/co-work#jobs" (sidebar, dashboards) scroll to that section once the page has content;
  // "/co-work?find=1" (sidebar) opens the seller search window.
  useEffect(() => {
    if (searchParams.get("find") === "1") setFindOpen(true);
  }, [searchParams]);

  useEffect(() => {
    if (!hash || jobs === null) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
  }, [hash, jobs]);

  const categories = (settings?.categories || "").split("\n").map((c) => c.trim()).filter(Boolean);
  const currency = settings?.currency_label || "USD";
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  // Banner button 1: "Register" for visitors, "Post a Job" for signed-in members.
  const handlePrimary = () => {
    setNotice("");
    if (!user) return setModal({ mode: "register", role: "buyer" });
    if (user.role === "buyer") return navigate("/co-work/dashboard?new=1");
    if (user.is_staff && !user.role) return setNotice("You are signed in as an administrator. Manage the marketplace from the admin panel.");
    setNotice("Only buyer accounts can post jobs. You are signed in as a seller - browse the open jobs below, or register a separate buyer account.");
    scrollTo(jobsRef);
  };

  // Banner button 2: "Find Seller" opens the seller search window (with its filters open).
  const handleFindSeller = () => {
    setNotice("");
    setFindOpen(true);
  };

  const closeFind = () => {
    setFindOpen(false);
    if (searchParams.get("find")) setSearchParams({}, { replace: true });
  };

  const handleMessageSeller = async (seller) => {
    setNotice("");
    if (!user) {   // visitors can search, but chatting needs a buyer account
      closeFind();
      return setModal({ mode: "register", role: "buyer" });
    }
    if (user.role !== "buyer") {
      closeFind();
      return setNotice("Only buyer accounts can start a chat with a seller.");
    }
    const error = await openChat(seller.user_id, navigate);
    if (error) {
      closeFind();
      setNotice(error);
    }
  };

  const handleAuthenticated = async () => {
    setModal(null);
    await refresh();
    navigate("/co-work/dashboard");
  };

  return (
    <>
      <Header logo={settings?.logo} />
      <div className="h-16" />

      <CoWorkSlider
        slides={slides}
        settings={settings}
        user={user}
        onPrimary={handlePrimary}
        onFindSeller={handleFindSeller}
      />

      {notice && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-sm">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 flex items-start justify-between gap-4">
            <p>{notice}</p>
            <button onClick={() => setNotice("")} aria-label="Dismiss" className="text-amber-200/70 hover:text-white shrink-0"><FiX /></button>
          </div>
        </div>
      )}

      <div className="bg-navy-dark text-white py-14">
        <MemberShell user={user} onSignOut={signOut}>
          <div>
            {/* Jobs board */}
            <section id="jobs" ref={jobsRef} className="scroll-mt-20">
              <h2 className="text-3xl md:text-4xl font-bold text-center">{settings?.jobs_heading || "Latest Jobs"}</h2>
              <p className="text-gray-400 text-center mt-2 mb-8">{settings?.jobs_intro}</p>

              <div className="flex flex-wrap gap-3 mb-8">
                <div className="relative flex-1 min-w-[220px]">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search jobs, skills..." className={`${inputClass} w-full pl-9`} />
                </div>
                <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputClass} bg-navy-dark`}>
                  <option value="">All categories</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {jobs === null ? (
                <p className="text-center text-gray-500 py-10">Loading jobs...</p>
              ) : jobs.length === 0 ? (
                <div className={`${cardClass} text-center py-14 px-6`}>
                  <p className="text-lg font-semibold">No open jobs right now</p>
                  <p className="text-gray-400 mt-1 mb-5">{category || query ? "Try a different search." : "Be the first to post a job."}</p>
                  <button onClick={handlePrimary} className="px-6 py-2.5 rounded-full bg-brand-gradient font-semibold">
                    {user ? settings?.buyer_button || "Post a Job" : settings?.register_button || "Register"}
                  </button>
                </div>
              ) : (
                <div className="grid gap-5">
                  {jobs.map((job, i) => <JobCard key={job.id} job={job} currency={currency} index={i} />)}
                </div>
              )}
            </section>
          </div>
        </MemberShell>
      </div>

      <Footer />

      {findOpen && <SellerSearchModal settings={settings} onClose={closeFind} onMessage={handleMessageSeller} />}

      {modal && (
        <AuthModal
          initialMode={modal.mode}
          initialRole={modal.role}
          settings={settings}
          onClose={() => setModal(null)}
          onAuthenticated={handleAuthenticated}
        />
      )}
    </>
  );
}
